'use client';

import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {useEffect, useRef, useState} from 'react';
import {trackAboutVideoUnmute} from '../../analytics';
import {MediaType, type Media} from '../../model';
import {AppIcon, FaVolumeOffIcon, FaVolumeUpIcon} from '../icon';

export const OVERVIEW_ASPECT = '1 / 1';
const VIDEO_PRELOAD = 'metadata';

const ACCENT_WIDTH = 2;
const ACCENT_GAP_MOBILE = 8;
const ACCENT_GAP_DESKTOP = 14;

const MediaFrame = styled(Box, {
  shouldForwardProp: propName => propName !== 'fullBleed' && propName !== 'circle' && propName !== 'elevated' && propName !== 'accent',
})<{
  fullBleed?: boolean;
  circle?: boolean;
  elevated?: boolean;
  accent?: boolean;
}>(({theme, fullBleed, circle, elevated, accent}) => {
  let borderRadius: string | number = theme.radii['4xl'];
  if (circle) {
    borderRadius = '50%';
  } else if (fullBleed) {
    borderRadius = 0;
  }
  return {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius,
    backgroundColor: theme.palette.colors.surface.neutral.subtle,
    boxShadow: elevated ? theme.customShadows.xl : 'none',
    // The accent is an `outline`, not a pseudo-element: an outline tracks borderRadius on
    // its own, so the ring stays concentric with whichever radius applies above instead of
    // restating it. It also sits outside the overflow clip and outside layout, so it needs
    // no wrapper and cannot be cropped by the frame it surrounds.
    ...(accent && {
      outline: `${ACCENT_WIDTH}px solid ${theme.palette.colors.border.primary.subtle}`,
      outlineOffset: ACCENT_GAP_MOBILE,
      [theme.breakpoints.up('md')]: {outlineOffset: ACCENT_GAP_DESKTOP},
    }),
  };
});

const MediaImage = styled('img')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const MediaVideo = styled('video')({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const SoundToggle = styled('button', {shouldForwardProp: propName => propName !== 'circle'})<{circle?: boolean}>(({theme, circle}) => ({
  position: 'absolute',
  insetInlineEnd: circle ? '12%' : theme.spacing(2),
  bottom: circle ? '12%' : theme.spacing(2),
  width: 40,
  height: 40,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  cursor: 'pointer',
  borderRadius: theme.radii.full,
  opacity: 0.8,
  background: theme.palette.colors.surface.darkOverlayStrong,
  color: theme.palette.colors.icon.neutral.inverse,
  '&:hover': {opacity: 1},
  '&:focus-visible': {outline: `2px solid ${theme.palette.colors.border.focus.onColor}`, outlineOffset: 2},
}));

export interface SectionMediaProps {
  media: Media;
  alt: string;
  aspectRatio: string | Record<string, string>;
  fullBleed?: boolean;
  circle?: boolean;
  elevated?: boolean;
  accent?: boolean;
  sectionName: string;
  sectionId?: string;
  height?: number | string | Record<string, number | string>;
}

export function SectionMedia({
  media,
  alt,
  aspectRatio,
  fullBleed,
  circle,
  elevated,
  accent,
  sectionName,
  sectionId,
  height,
}: SectionMediaProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (media.type !== MediaType.Video) {
      return;
    }
    const video = videoRef.current;
    const frame = frameRef.current;
    if (!video || !frame) {
      return;
    }
    video.muted = true;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void video.play();
        } else {
          video.pause();
        }
      },
      {threshold: 0.4},
    );
    observer.observe(frame);
    return () => {
      observer.disconnect();
    };
  }, [media.type]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = !video.muted;
    setMuted(video.muted);
    if (!video.muted) {
      trackAboutVideoUnmute({name: sectionName, id: sectionId});
    }
  };

  return (
    <MediaFrame ref={frameRef} fullBleed={fullBleed} circle={circle} elevated={elevated} accent={accent} sx={{aspectRatio, height}}>
      {media.type === MediaType.Video ? (
        <>
          <MediaVideo ref={videoRef} src={media.src} poster={media.poster} loop playsInline muted preload={VIDEO_PRELOAD} />
          <SoundToggle circle={circle} type="button" onClick={toggleMute}>
            <AppIcon icon={muted ? FaVolumeOffIcon : FaVolumeUpIcon} sx={{fontSize: '18px'}} aria-hidden />
          </SoundToggle>
        </>
      ) : (
        <MediaImage src={media.src} alt={alt} loading="lazy" />
      )}
    </MediaFrame>
  );
}

SectionMedia.displayName = 'SectionMedia';
